import React, { useEffect, useState } from 'react'
import { track } from '@amplitude/analytics-browser'
import { Button, Panel, colors } from 'src/components/app-ui'
import UserType from 'src/model/user-types'
import useStyles from './styles'

const CODE_TABS = [
  {
    value: 'css',
    label: 'CSS',
    hint: 'Added inside <style> in the page head',
    placeholder: '/* Example */\n.mcg-button { border-radius: 999px; }',
  },
  {
    value: 'js',
    label: 'JavaScript',
    hint: 'Added before </body>, no <script> tag needed',
    placeholder: '// Example\nwindow.dataLayer = window.dataLayer || [];',
  },
]

/** Same plan rule as landing page custom code (Basic and trial are locked). */
const hasCustomCodeAccess = () => {
  const plan = UserType[window.CONSTANTS?.USER_ROLE_ID]
  return plan !== 'Basic' && !UserType.isTrialEquivalent(plan)
}

const upgrade = () => {
  track('limit_warning_upgrade', { from: 'workspace_custom_code_upgrade' })
  window.location.href = '/pricing'
}

/**
 * Workspace CSS / JS injected into every course and landing page. Has its own
 * Save; Cancel drops the changes made since the last save.
 */
const CustomCodeCard = ({ branding, onSave }) => {
  const classes = useStyles()
  const allowed = hasCustomCodeAccess()
  const [tab, setTab] = useState('css')
  const [code, setCode] = useState({ css: '', js: '' })
  const [saving, setSaving] = useState(false)

  const saved = { css: branding.customCss || '', js: branding.customJs || '' }

  useEffect(() => {
    setCode({ css: branding.customCss || '', js: branding.customJs || '' })
  }, [branding.customCss, branding.customJs])

  const dirty = code.css !== saved.css || code.js !== saved.js
  const active = CODE_TABS.find(item => item.value === tab)

  const handleSave = () => {
    setSaving(true)
    onSave(code).finally(() => setSaving(false))
  }

  return (
    <Panel className={classes.card}>
      <div className={classes.codeHead}>
        <div>
          <div className={classes.cardTitle}>
            Custom code
            {!allowed && <span className={classes.plus}>Plus</span>}
          </div>
          <div className={classes.cardDesc}>
            Injected into every course and landing page in this workspace, so
            you don't have to paste it into each one. Code added on a single
            course or landing page still works and loads after this.
          </div>
        </div>
      </div>

      <div className={classes.editor}>
        <div className={classes.editorBar}>
          <div style={{ display: 'flex', gap: 4 }}>
            {CODE_TABS.map(item => (
              <button
                key={item.value}
                type="button"
                className={classes.editorTab}
                style={{
                  background:
                    tab === item.value ? colors.primarySoft : 'transparent',
                  color: tab === item.value ? colors.primary : colors.muted,
                }}
                onClick={() => setTab(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <span className={classes.editorHint}>{active.hint}</span>
        </div>
        <textarea
          className={classes.textarea}
          spellCheck={false}
          disabled={!allowed}
          value={code[tab]}
          placeholder={active.placeholder}
          onChange={event =>
            setCode(current => ({ ...current, [tab]: event.target.value }))
          }
        />
      </div>

      <div className={classes.codeFooter}>
        <span>Scripts run in learners' browsers. Only add code you trust.</span>
        <div className={classes.actions}>
          {allowed ? (
            <>
              <Button
                variant="ghost"
                size="small"
                disabled={!dirty || saving}
                onClick={() => setCode(saved)}
              >
                Cancel
              </Button>
              <Button
                size="small"
                disabled={!dirty || saving}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button size="small" icon="tabler:lock" onClick={upgrade}>
              Upgrade
            </Button>
          )}
        </div>
      </div>
    </Panel>
  )
}

export default CustomCodeCard
